<template>
  <div style="border: 1px solid #ccc; resize: vertical; overflow: auto; min-height: 220px;">
    <Toolbar
      style="border-bottom: 1px solid #dcdfe6; width: 100%; border-radius: 4px"
      :editor="editorRef"
      mode="default"
    />
    <Editor
      style="min-height: 200px; max-height: none"
      v-model="state.value"
      @onCreated="handleCreated"
      @customPaste="customPaste"
      :default-config="editorConfig"
      :key="state.key"
    />
  </div>
</template>

<script>
import { onMounted, reactive, shallowRef, ref, watch, nextTick } from "vue";
import { ElMessage } from "element-plus";
import "@wangeditor/editor/dist/css/style.css"; // 引入 css
import { Editor, Toolbar } from "@wangeditor/editor-for-vue";
import $http from "@/http/index";

export default {
  props: {
    value: {
      type: String,
    },
    placeholder: {
      type: String,
      default: "请输入内容...",
    },
  },
  emits: ["update:value"],
  components: {
    Editor,
    Toolbar,
  },
  setup(props, { emit }) {
    const state = reactive({});
    const editorRef = shallowRef();
    let isInternalUpdate = false;

    const handleCreated = (editor) => {
      editorRef.value = editor; // 记录 editor 实例，重要！
      // 编辑器创建完成后，直接通过 setHtml 设置初始内容
      nextTick(() => {
        if (props.value) {
          editor.setHtml(props.value);
        }
      });
    };

    onMounted(() => {
      // 兜底：如果 handleCreated 后仍没内容，再用 setHtml
      setTimeout(() => {
        if (editorRef.value && props.value && !editorRef.value.getHtml()) {
          editorRef.value.setHtml(props.value);
        }
      }, 500);
    });

    // 监听外部 value 变化，通过 setHtml 同步到编辑器
    watch(
      () => props.value,
      (newVal) => {
        if (!isInternalUpdate && newVal !== undefined) {
          const currentHtml = editorRef.value?.getHtml();
          if (currentHtml !== undefined && currentHtml !== newVal) {
            editorRef.value.setHtml(newVal || "");
          }
        }
      }
    );

    // 监听编辑器内容变化，同步到外部
    watch(
      () => state.value,
      (newVal) => {
        if (newVal !== undefined) {
          isInternalUpdate = true;
          emit("update:value", newVal);
          nextTick(() => {
            isInternalUpdate = false;
          });
        }
      }
    );

    // ==================== 公告专用：图片上传函数 ====================
    // 通过 $http 走 axios 拦截器，携带 token；Content-Type 设为 undefined 让浏览器自动加 boundary，避免 413
    const uploadImageToServer = (file) => {
      const formData = new FormData();
      formData.append("multipartFile", file);
      return $http.post("/Home/UploadImages", formData, {
        // 注意：Content-Type 设为 undefined，让浏览器自动添加正确的 boundary
        headers: { "Content-Type": undefined },
      });
    };

    // 粘贴图片处理：拦截粘贴事件，将图片上传到服务器后插入URL
    const customPaste = (editor, event) => {
      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const items = clipboardData.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          // 阻止默认粘贴行为（避免插入base64）
          event.preventDefault();

          const file = item.getAsFile();
          if (!file) continue;

          uploadImageToServer(file).then((res) => {
            if (res.status === 200 && res.data && res.data[0]) {
              const imgUrl = res.data[0];
              editor.insertNode({
                type: 'image',
                src: imgUrl,
                alt: file.name || '',
                href: '',
                children: [{ text: '' }],
              });
              ElMessage.success("图片插入成功");
            } else {
              ElMessage.error("图片上传返回异常");
              console.warn("图片上传返回异常", res);
            }
          }).catch((err) => {
            ElMessage.error("粘贴图片上传失败");
            console.warn('粘贴图片上传失败', err);
          });

          return false; // 只处理第一张图片
        }
      }
    };

    const editorConfig = ref({
      placeholder: props.placeholder,
      readOnly: false, // 只读
      MENU_CONF: {
        // 配置上传图片（公告专用）
        uploadImage: {
          // 不使用默认 server 方式（绕过 axios、不带 token），改用 customUpload
          maxFileSize: 10 * 1024 * 1024, // 10M  图片大小限制
          fieldName: "multipartFile", // 上传名字
          allowedFileTypes: ['image/*'], // 选择文件时的类型限制，必须显式指定 image/* 才能弹出图片选择器
          // 自定义上传：通过项目封装的 $http 上传，携带 token、走统一拦截器
          // wangEditor v5: 回调签名 customUpload(file, insertFn)，成功后必须调用 insertFn(url, alt, href) 让编辑器插入图片
          customUpload(file, insertFn) {
            // 大小兜底校验
            if (file.size > 10 * 1024 * 1024) {
              ElMessage.error("图片大小不能超过 10M");
              return;
            }
            uploadImageToServer(file)
              .then((res) => {
                if (res.status === 200 && res.data && res.data[0]) {
                  insertFn(res.data[0], file.name, "");
                  ElMessage.success("图片上传成功");
                } else {
                  ElMessage.error("图片上传返回异常");
                  console.warn("图片上传返回异常", res);
                }
              })
              .catch((err) => {
                ElMessage.error("图片上传失败");
                console.warn("图片上传失败", err);
              });
          },
        },
      },
    });

    return { state, props, handleCreated, editorRef, editorConfig, customPaste };
  },
};
</script>
<style scoped></style>
